/**
 * Sign in and change-password.
 *
 * Both screens end by re-reading who is signed in rather than by assuming. Signing in
 * changes which reads are privileged, which routes are writable, and what the masthead
 * offers, and one `refresh()` is the cheapest way to be sure nothing stale survives it.
 */

import { useState } from "react";
import { ApiError, api } from "../api";
import {
  Button,
  Card,
  Field,
  Input,
  Link,
  Notice,
  Row,
  Stack,
  Warning,
  navigate,
  useAuth,
  useQueryParam,
} from "../components";

export function LoginPage() {
  const auth = useAuth();
  const next = useQueryParam("next");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.login(username.trim(), password);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Sign in failed; try again");
      setBusy(false);
      return;
    }
    navigate(next && next.startsWith("/") ? next : "/", { replace: true });
    await auth.refresh();
  };

  return (
    <Stack>
      <Card title="Sign in">
        <form onSubmit={submit}>
          <Stack>
            {error ? <Warning>{error}</Warning> : null}
            <Field label="Username" htmlFor="username">
              <Input
                id="username"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                disabled={busy}
              />
            </Field>
            <Field label="Password" htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={busy}
              />
            </Field>
            <Button type="submit" variant="primary" busy={busy} disabled={!username || !password}>
              Sign in
            </Button>
            <Notice>
              You do not need an account to shoot. Sign up on the league&rsquo;s page; a scorer
              approves it at the range.
            </Notice>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}

export function ChangePasswordPage({ forced = false }: { forced?: boolean }) {
  const auth = useAuth();
  const [current, setCurrent] = useState("");
  const [fresh, setFresh] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = fresh.length > 0 && fresh.length < 10;
  const mismatch = confirm.length > 0 && fresh !== confirm;
  const unchanged = fresh.length > 0 && fresh === current;
  const ready = current.length > 0 && fresh.length >= 10 && fresh === confirm;

  const submit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, fresh);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "The password was not changed");
      setBusy(false);
      return;
    }
    // Every other session was revoked and this one is no longer flagged, so the shell has to
    // learn both from scratch.
    await auth.refresh();
    if (forced) navigate("/", { replace: true });
  };

  return (
    <Stack>
      <Card title={forced ? "Choose a password" : "Change password"}>
        <form onSubmit={submit}>
          <Stack>
            {forced ? (
              <p>
                This account still has the password it was created with. Nothing can be written
                until it is replaced.
              </p>
            ) : (
              <p>Changing it signs every other device out.</p>
            )}
            {error ? <Warning>{error}</Warning> : null}
            <Field label="Current password" htmlFor="current">
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                disabled={busy}
              />
            </Field>
            <Field
              label="New password"
              htmlFor="fresh"
              error={
                tooShort
                  ? "Ten characters at least"
                  : unchanged
                    ? "That is the password you already have"
                    : undefined
              }
              hint="A phrase you can type beats a symbol soup you will write down."
            >
              <Input
                id="fresh"
                type="password"
                autoComplete="new-password"
                value={fresh}
                onChange={(event) => setFresh(event.target.value)}
                disabled={busy}
              />
            </Field>
            <Field
              label="Type it again"
              htmlFor="confirm"
              error={mismatch ? "These do not match" : undefined}
            >
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                disabled={busy}
              />
            </Field>
            <Row>
              <Button type="submit" variant="primary" busy={busy} disabled={!ready}>
                Save password
              </Button>
              {forced ? <Link to="/">Not now</Link> : null}
            </Row>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}
